import { defineStore } from 'pinia'
import { authApi } from '../api/auth.api'
import { useUserStore } from '@/modules/user/stores/user.store'
import { IdentityStatus } from '@/modules/user/types/user.enums'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import type { RouteLocationRaw } from 'vue-router'
import type { HttpResponse } from '@/services/http'
import type { AuthErrorCode } from '../types/auth.enums'

const AUDIENCE_URL = import.meta.env.VITE_AUTH_AUDIENCE_URL
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID
const ISSUER_URL = import.meta.env.VITE_AUTH_ISSUER_URL
const AUTH_PATH = import.meta.env.VITE_AUTH_AUTHORIZE_PATH
const REDIRECT_URI = import.meta.env.VITE_AUTH_REDIRECT_URI
const RETURN_TO_URL = import.meta.env.VITE_AUTH_RETURN_TO_URL
const LOGOUT_PATH = import.meta.env.VITE_AUTH_LOGOUT_PATH
const SCOPE = import.meta.env.VITE_AUTH_SCOPE
const HOST = `${window.location.protocol}//${window.location.host}`

export const useAuthStore = defineStore('auth', () => {
  const userStore = useUserStore()

  const exchangeCode = async (
    code: string,
    state: string,
  ): Promise<HttpResponse<null, AuthErrorCode>> => {
    return await authApi.exchangeCode(code, state, HOST)
  }

  const exchangeSession = async (): Promise<HttpResponse<null, AuthErrorCode>> => {
    return await authApi.exchangeSession()
  }

  const navToLogout = (): void => {
    const url = new URL(`${ISSUER_URL}${LOGOUT_PATH}`)
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('returnTo', `${HOST}${RETURN_TO_URL}`)
    window.location.href = url.toString()
  }

  const logout = async (): Promise<void> => {
    userStore.clear()
    await authApi.tryLogout()
  }

  const navToLogin = (): void => {
    const url = new URL(`${ISSUER_URL}${AUTH_PATH}`)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('redirect_uri', `${HOST}${REDIRECT_URI}`)
    url.searchParams.set('scope', SCOPE)
    url.searchParams.set('state', crypto.randomUUID())
    url.searchParams.set('audience', AUDIENCE_URL)
    url.searchParams.set('prompt', 'select_account')
    url.searchParams.set('loginHint', '')
    window.location.href = url.toString()
  }

  const getRouteLocation = (state?: IdentityStatus): RouteLocationRaw | boolean => {
    switch (state) {
      case IdentityStatus.SignIn:
        navToLogin()
        return false
      case IdentityStatus.Authenticated:
        return true
      case IdentityStatus.Blocked:
        return { name: ROUTE_NAMES.BLOCKED }
      case IdentityStatus.Activation:
        return { name: ROUTE_NAMES.ACTIVATE }
      case IdentityStatus.Registration:
        return { name: ROUTE_NAMES.REGISTER }
      default:
        return false
    }
  }

  return {
    exchangeCode,
    exchangeSession,
    logout,
    navToLogin,
    navToLogout,
    getRouteLocation,
  }
})
